import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import ConfirmModal from '../../components/ConfirmModal';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import './AdminCategories.css';

const PRESET_COLORS = ['#6C3AC7','#A855F7','#0EA5E9','#10B981','#F59E0B','#EC4899','#F97316','#22C55E','#3B82F6','#06B6D4'];

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', color: '#6C3AC7', order: 99 });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try { setLoading(true); const r = await API.get('/categories'); setCategories(r.data.categories || []); }
    catch { toast.error('Gagal memuat kategori'); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditCat(null); setForm({ name: '', slug: '', color: '#6C3AC7', order: 99 }); setShowModal(true); };
  const openEdit = (c) => { setEditCat(c); setForm({ name: c.name, slug: c.slug, color: c.color || '#6C3AC7', order: c.order || 99 }); setShowModal(true); };

  const autoSlug = (name) => name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nama kategori wajib diisi'); return; }
    try {
      setSaving(true);
      if (editCat) { await API.patch(`/categories/${editCat._id}`, form); toast.success('Kategori diperbarui'); }
      else { await API.post('/categories', form); toast.success('Kategori dibuat'); }
      setShowModal(false); fetchCategories();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    try { await API.delete(`/categories/${c._id}`); toast.success('Kategori dihapus'); fetchCategories(); }
    catch (err) { toast.error(err.response?.data?.message || 'Gagal menghapus'); }
    finally { setDeleteTarget(null); }
  };

  return (
    <DashboardLayout title="Kelola Kategori" subtitle="Atur kategori yang tersedia untuk artikel">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={14} /> Tambah Kategori</button>
      </div>

      {loading ? (
        <div className="ki-card">{[...Array(5)].map((_, i) => <div key={i} className="shimmer" style={{ height: 48, borderRadius: 8, marginBottom: 10 }} />)}</div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead><tr><th>Warna</th><th>Nama</th><th>Slug</th><th>Urutan</th><th>Aksi</th></tr></thead>
            <tbody>
              {categories.map(c => (
                <tr key={c._id}>
                  <td><div style={{ width: 24, height: 24, borderRadius: '50%', background: c.color || '#6C3AC7' }} /></td>
                  <td style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</td>
                  <td style={{ fontSize: 12, color: 'var(--ki-text-faint)', fontFamily: 'monospace' }}>{c.slug}</td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-muted)' }}>{c.order || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)}><Edit2 size={13} /></button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTarget(c)}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {categories.length === 0 && <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--ki-text-muted)', fontSize: 14 }}>Belum ada kategori.</div>}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="modal-head">
              <div style={{ fontSize: 17, fontWeight: 700 }}>{editCat ? 'Edit Kategori' : 'Tambah Kategori'}</div>
              <button className="btn btn-icon btn-ghost" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Nama Kategori *</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value, slug: autoSlug(e.target.value) })} placeholder="Contoh: Teknologi" />
              </div>
              <div className="form-group">
                <label className="form-label">Slug (auto-generate)</label>
                <input className="form-input" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="teknologi" />
              </div>
              <div className="form-group">
                <label className="form-label">Warna</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                  {PRESET_COLORS.map(color => (
                    <button key={color} onClick={() => setForm({ ...form, color })} style={{ width: 28, height: 28, borderRadius: '50%', background: color, border: form.color === color ? '3px solid white' : '2px solid transparent', cursor: 'pointer', outline: 'none' }} />
                  ))}
                </div>
                <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} style={{ marginTop: 8, width: '100%', height: 36, border: '1px solid var(--ki-border)', borderRadius: 8, background: 'none', cursor: 'pointer' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Urutan</label>
                <input className="form-input" type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 99 })} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
                <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Menyimpan...' : editCat ? 'Perbarui' : 'Buat Kategori'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        open={!!deleteTarget}
        title="Hapus Kategori"
        message={`Hapus kategori "${deleteTarget?.name}"? Artikel yang menggunakan kategori ini akan terpengaruh.`}
        confirmLabel="Hapus"
        onConfirm={() => handleDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </DashboardLayout>
  );
}
